import React, { FC, useCallback, useMemo } from "react";

import { faArrowsFromLine, faArrowsToLine } from "@fortawesome/pro-solid-svg-icons";
import { faColumns } from "@fortawesome/pro-solid-svg-icons/faColumns";
import { faCopy } from "@fortawesome/pro-solid-svg-icons/faCopy";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { faMinusCircle } from "@fortawesome/pro-solid-svg-icons/faMinusCircle";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps as AntDMenuProps } from "antd/es/menu";
import { defineMessages, useIntl } from "react-intl";

import MoreButton from "@app/component/MoreButton";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

const messages = defineMessages({
  copy: {
    id: "dashboard.cards.menu.copy",
    defaultMessage: "Create a Copy",
    description: "Create a Copy chart menu item in the cards' menu",
  },
  downloadData: {
    id: "dashboard.cards.menu.downloadData",
    defaultMessage: "Download Data",
    description: "Download Data menu item in the cards' menu",
  },
  edit: {
    id: "dashboard.cards.menu.edit",
    defaultMessage: "Edit Chart",
    description: "Edit Chart menu item in the cards' menu",
  },
  expand: {
    id: "dashboard.cards.menu.expand",
    defaultMessage: "Expand",
    description: "Expand chart menu item in the cards' menu",
  },
  remove: {
    id: "dashboard.cards.menu.remove",
    defaultMessage: "Remove Chart",
    description: "Remove Chart menu item in the cards' menu",
  },
  shrink: {
    id: "dashboard.cards.menu.shrink",
    defaultMessage: "Shrink",
    description: "Shrink chart menu item in the cards' menu",
  },
  viewData: {
    id: "dashboard.cards.menu.viewData",
    defaultMessage: "View Data",
    description: "View Data menu item in the cards' menu",
  },
});

export interface MenuProps {
  expanded?: boolean;
  onCopy?: () => void;
  onDownloadData?: () => void;
  onEdit?: () => void;
  onRemove?: () => void;
  onResize?: (fullWidth: boolean) => void;
  onViewData?: () => void;
}

const Menu: FC<MenuProps> = ({
  expanded,
  onCopy,
  onDownloadData,
  onEdit,
  onRemove,
  onResize,
  onViewData,
}) => {
  const intl = useIntl();
  const analytics = useExtendedAnalytics("Chart ellipsis", useAnalytics());

  const menu = useMemo(() => {
    const menuProps: AntDMenuProps = { items: [] };
    if (onViewData) {
      menuProps.items!.push({
        icon: <FontAwesomeIcon icon={faColumns} />,
        key: "viewData",
        label: intl.formatMessage(messages.viewData),
        onClick: () => {
          analytics.clicked(["view data"]);
          onViewData();
        },
      });
    }
    if (onResize) {
      menuProps.items!.push({
        icon: <FontAwesomeIcon icon={expanded ? faArrowsToLine : faArrowsFromLine} rotation={90} />,
        key: "resize",
        label: intl.formatMessage(expanded ? messages.shrink : messages.expand),
        onClick: () => {
          analytics.clicked([expanded ? "shrink" : "expand"]);
          onResize(!expanded);
        },
      });
    }
    if (onDownloadData) {
      menuProps.items!.push({
        icon: <FontAwesomeIcon icon={faDownload} />,
        key: "downloadData",
        label: intl.formatMessage(messages.downloadData),
        onClick: () => {
          analytics.clicked(["download data"]);
          onDownloadData();
        },
      });
    }
    if (onEdit) {
      menuProps.items!.push({
        icon: <FontAwesomeIcon icon={faPen} />,
        key: "edit",
        label: intl.formatMessage(messages.edit),
        onClick: () => {
          analytics.clicked(["edit chart"]);
          onEdit();
        },
      });
    }
    if (onCopy) {
      menuProps.items!.push({
        icon: <FontAwesomeIcon icon={faCopy} />,
        key: "copy",
        label: intl.formatMessage(messages.copy),
        onClick: () => {
          analytics.clicked(["copy chart"]);
          onCopy();
        },
      });
    }
    if (onRemove) {
      menuProps.items!.push({
        danger: true,
        icon: <FontAwesomeIcon icon={faMinusCircle} />,
        key: "remove",
        label: intl.formatMessage(messages.remove),
        onClick: () => {
          analytics.clicked(["remove chart"]);
          onRemove();
        },
      });
    }
    return menuProps;
  }, [onViewData, onResize, onDownloadData, onEdit, onCopy, onRemove, intl, analytics, expanded]);

  const handleOpenChange = useCallback(
    (open) => {
      if (open) {
        analytics.clicked([]);
      }
    },
    [analytics]
  );

  if (!menu.items?.length) {
    return null;
  }

  return (
    <Dropdown menu={menu} onOpenChange={handleOpenChange} trigger={["click"]}>
      <MoreButton />
    </Dropdown>
  );
};

export default Menu;
